"use client";

import React from "react";
import { OctagonAlert as AlertOctagon, ArrowRight, Droplets, Flame, LifeBuoy, Pipette, PhoneCall, ShieldAlert, Wrench } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface ProblemCategory {
  id: string;
  title: string;
  badge: string;
  description: string;
  commonSigns: string;
  urgency: string;
  linkPath: string;
}
const problemCategories: ProblemCategory[] = [{
  id: "burst-pipes",
  title: "Burst & Leaking Pipes",
  badge: "Immediate Action",
  description: "Active wall leaks, frozen burst lines, ceiling leaks, and pressurized pipe ruptures.",
  commonSigns: "Hissing behind walls, wet drywall, rapid meter spin, pooling water.",
  urgency: "High Damage Risk",
  linkPath: "/problems/"
}, {
  id: "sewer-backup",
  title: "Main Sewer Line Backups",
  badge: "Health Hazard",
  description: "Multiple clogged fixtures, sewage coming up bathtub or basement floor drains.",
  commonSigns: "Foul odor, gurgling toilets when running sinks, standing dirty water.",
  urgency: "Critical Emergency",
  linkPath: "/problems/"
}, {
  id: "water-heaters",
  title: "Water Heater Breakdown",
  badge: "Same-Day Fix",
  description: "Total lack of hot water, tank flooding, pilot failure, or strange rumbling sounds.",
  commonSigns: "Lukewarm water, rust-colored water, leaking base, tripped breaker.",
  urgency: "Urgent Repair",
  linkPath: "/water-heater/"
}, {
  id: "drain-clogs",
  title: "Severe Drain Obstructions",
  badge: "Hydro-Jetting Ready",
  description: "Solid blockages in kitchen stacks, master baths, or main building outlets.",
  commonSigns: "Slow drainage across fixtures, recurring overflows, localized standing water.",
  urgency: "High Priority",
  linkPath: "/problems/"
}, {
  id: "overflowing-toilets",
  title: "Toilet & Fixture Overflows",
  badge: "Rapid Response",
  description: "Uncontrollable bowl fill, broken shutoff valves, or continuous high-volume leaking.",
  commonSigns: "Water spilling onto flooring, valve won't shut completely, loose base.",
  urgency: "Immediate Hazard",
  linkPath: "/problems/"
}, {
  id: "low-pressure",
  title: "Sudden Water Pressure Drop",
  badge: "Diagnostic Needed",
  description: "Abrupt loss of flow throughout the entire home or specific hot-water supply branches.",
  commonSigns: "Trickling faucets, sputtering showers, possible hidden mainline crack.",
  urgency: "Diagnostic Service",
  linkPath: "/problems/"
}];
export default function RelatedProblems() {
  return <section id="relatedproblemcategories" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wide uppercase border-border">
            Plumbing Diagnostics & Solutions
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Explore Related Plumbing Problems
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Diagnose your issue quickly. Select any category below to understand symptoms, risks, and immediate containment steps before our technician arrives.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {problemCategories.map((item, i) => <Card key={item.id} data-index={i} className="bg-card border-border hover:border-accent transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md flex flex-col justify-between">
              <CardHeader>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="inline-flex items-center justify-center size-10 rounded-lg bg-secondary text-secondary-foreground font-semibold">
                    {i === 0 && <Droplets className="size-5 text-primary" />}
                    {i === 1 && <ShieldAlert className="size-5 text-destructive" />}
                    {i === 2 && <Flame className="size-5 text-chart-2" />}
                    {i === 3 && <Pipette className="size-5 text-primary" />}
                    {i === 4 && <AlertOctagon className="size-5 text-destructive" />}
                    {i === 5 && <Wrench className="size-5 text-primary" />}
                  </span>
                  <Badge variant="secondary" className="text-xs font-medium">
                    {item.badge}
                  </Badge>
                </div>
                <CardTitle className="text-xl font-bold font-default text-card-foreground">
                  {item.title}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm mt-1 leading-normal">
                  {item.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3 pt-0">
                <div className="rounded-md bg-muted/60 p-3 border border-border/50 text-xs">
                  <span className="font-semibold text-foreground block mb-1">Common Symptoms:</span>
                  <p className="text-muted-foreground leading-relaxed">{item.commonSigns}</p>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Priority Level:</span>
                  <span className="font-medium text-foreground">{item.urgency}</span>
                </div>
              </CardContent>

              <CardFooter className="pt-2 border-t border-border">
                <Button asChild variant="ghost" className="w-full justify-between hover:bg-secondary text-foreground group">
                  <Link to={item.linkPath}>
                    <span>View Troubleshooting & Guides</span>
                    <ArrowRight className="size-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        {/* Urgent Bottom Help Banner */}
        <div className="mt-12 sm:mt-16 rounded-2xl bg-secondary/70 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg sm:text-xl font-bold text-foreground font-default flex items-center justify-center sm:justify-start gap-2">
              <LifeBuoy className="size-5 text-primary shrink-0" />
              Not sure which problem category matches your leak?
            </h3>
            <p className="text-sm text-muted-foreground max-w-xl">
              Talk directly with a local Jonesboro master plumber right now. We provide instant phone triage and 24/7 on-site emergency dispatch.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/contact/">
                <Wrench className="size-4" />
                Request Service
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-semibold bg-background">
              <a href="tel:4708479076">
                <PhoneCall className="size-4 text-primary" />
                Call 24/7 Plumber
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}